import { useState, useEffect, useCallback, useMemo } from "react"
import * as gqlTypes from "@hydrologiq/gql/graphql"
import { useQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"

interface AssetItem {
  id: string
  name: string
}

interface UseMultiAssetValidationProps {
  selectedAssets: AssetItem[]
  startDate: string
  endDate: string
  orderId?: string
  fulfillmentId?: string
  activitiesExclude?: string
}

interface UseMultiAssetValidationReturn {
  unavailableAssets: AssetItem[]
  isValidating: boolean
  revalidate: () => void
}

/**
 * Hook for validating multiple assets' availability during order date ranges
 * Used by OrderCreate and OrderEdit workflows to check if all selected assets
 * are available for the specified date range
 *
 * Simple approach: Use useQuery which handles its own caching and dedupe logic
 */
export function useMultiAssetValidation({
  selectedAssets,
  startDate,
  endDate,
  orderId,
  fulfillmentId,
  activitiesExclude,
}: UseMultiAssetValidationProps): UseMultiAssetValidationReturn {
  const [revalidateTrigger, setRevalidateTrigger] = useState<number>(0)

  // Skip query if no dates or no selected assets
  const skip = !startDate || !endDate || selectedAssets.length === 0

  // Use useQuery instead of useLazyQuery for simpler state management
  const {
    data: availabilityData,
    loading,
    error,
    refetch,
  } = useQueryWithEndpoint(
    gqlTypes.GetAvailableAssetsDocument,
    {
      variables: {
        start: startDate,
        end: endDate,
        order_exclude: orderId,
        fulfillment_exclude: fulfillmentId,
        activities_exclude: activitiesExclude,
      },
      skip,
      fetchPolicy: "cache-and-network",
    },
    "primary"
  )

  // Manual revalidation function
  const revalidate = useCallback(() => {
    if (!skip) {
      refetch()
    }
    setRevalidateTrigger((prev) => prev + 1)
  }, [skip, refetch])

  // Calculate unavailable assets - memoized to prevent unnecessary recalc
  const unavailableAssets = useMemo(() => {
    if (error || !availabilityData?.assets?.data || selectedAssets.length === 0) {
      return []
    }

    // Create a Set of available asset IDs for O(1) lookup
    const availableIds = new Set(availabilityData.assets.data.map((asset: any) => asset.id))

    // Find assets that are selected but not available
    return selectedAssets.filter((asset) => !availableIds.has(asset.id))
  }, [availabilityData, selectedAssets, error])

  return {
    unavailableAssets,
    isValidating: loading,
    revalidate,
  }
}
